import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  fetchCompanyById,
  clearSelectedCompany,
  selectCompanies,
  selectCompaniesLoading,
  selectCompaniesError,
  selectSelectedCompany
} from '../../store/slices/companiesSlice';
import { Building2, CreditCard, TrendingUp, Plus, MoreVertical, Mail, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Modal, StatsCard } from '../ui';
import Button from '../ui/Button';
import { apiService } from '../../services/api';
import { uploadCompanyLogo } from '../../store/slices/companiesSlice';
import type { AdminDashboardSummary, AdminCompanyRecord, CreateAdminCompanyRequest, UpdateAdminCompanyRequest } from '../../services/api';
import './CompanyPage.css';



interface CompanyActionDropdownProps {
  company: AdminCompanyRecord;
  onEdit: (company: AdminCompanyRecord) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
}

const CompanyActionDropdown: React.FC<CompanyActionDropdownProps> = ({ company, onEdit, onDelete, onView }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action: string) => {
    setIsOpen(false);
    if (action === 'edit') onEdit(company);
    if (action === 'delete') onDelete(company.id);
    if (action === 'view') onView(company.id);
  };

  return (
    <div className="company-action-dropdown">
      <button
        className="action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button className="dropdown-item" onClick={() => handleAction('view')}>View Details</button>
            <button className="dropdown-item" onClick={() => handleAction('edit')}>Edit Company</button>
            <button className="dropdown-item danger" onClick={() => handleAction('delete')}>Delete Company</button>
          </div>
        </>
      )}
    </div>
  );
};

const CompanyPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const companies = useAppSelector(selectCompanies);
  const isLoading = useAppSelector(selectCompaniesLoading);
  const error = useAppSelector(selectCompaniesError);
  const selectedCompany = useAppSelector(selectSelectedCompany);

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  const [newCompanyForm, setNewCompanyForm] = useState<CreateAdminCompanyRequest & { confirmPassword?: string }>({
    legalName: '',
    contactEmail: '',
    password: '',
    confirmPassword: '',
  });
  const [newCompanyLogo, setNewCompanyLogo] = useState<File | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [editCompanyForm, setEditCompanyForm] = useState<UpdateAdminCompanyRequest & { id: string }>({
    id: '',
    legalName: '',
    contactEmail: '',
  });
  const [editCompanyLogo, setEditCompanyLogo] = useState<File | null>(null);

  useEffect(() => {
    dispatch(fetchCompanies());

    const fetchSummary = async () => {
      try {
        setIsLoadingSummary(true);
        const data = await apiService.getDashboardSummary();
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch dashboard summary:', error);
      } finally {
        setIsLoadingSummary(false);
      }
    };

    fetchSummary();
  }, [dispatch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleAddCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newCompanyForm.password !== newCompanyForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      const { confirmPassword, ...submitData } = newCompanyForm;
      const newCompany = await dispatch(createCompany(submitData)).unwrap();
      
      if (newCompanyLogo) {
        await dispatch(uploadCompanyLogo({ id: newCompany.id, file: newCompanyLogo })).unwrap();
      }
      
      setIsAddModalOpen(false);
      setNewCompanyForm({ legalName: '', contactEmail: '', password: '', confirmPassword: '' });
      setNewCompanyLogo(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      alert(err || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { id, ...data } = editCompanyForm;
      await dispatch(updateCompany({ id, data })).unwrap();
      
      if (editCompanyLogo) {
         await dispatch(uploadCompanyLogo({ id, file: editCompanyLogo })).unwrap();
      }

      setIsEditModalOpen(false);
      setEditCompanyLogo(null);
    } catch (err: any) {
      alert(err || 'Failed to update company');
    }
  };

  const handleDeleteCompany = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await dispatch(deleteCompany(id)).unwrap();
      } catch (err: any) {
        alert(err || 'Failed to delete company');
      }
    }
  };

  const handleEditClick = (company: AdminCompanyRecord) => {
    setEditCompanyForm({
      id: company.id,
      legalName: company.legalName,
      contactEmail: company.contactEmail,
    });
    setIsEditModalOpen(true);
  };

  const handleViewClick = (id: string) => {
    dispatch(fetchCompanyById(id));
    setIsDetailsModalOpen(true);
  };

  const filteredCompanies = companies.filter(company =>
    company.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading && companies.length === 0) {
    return (
      <div className="company-page loading-state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div className="spinner" />
        <p>Loading companies...</p>
      </div>
    );
  }

  if (error && companies.length === 0) {
    return (
      <div className="company-page error-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
        <AlertCircle size={48} style={{ color: '#ef4444' }} />
        <h2>Failed to load companies</h2>
        <p>{error}</p>
        <Button variant="primary" onClick={() => dispatch(fetchCompanies())}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="company-page">
      <div className="company-header">
        <div className="company-title">
          <Building2 size={24} />
          <h1>Company Management</h1>
        </div>
        <Button variant="primary" className="add-company-btn" onClick={() => setIsAddModalOpen(true)}>
          <Plus size={16} />
          Add Company
        </Button>
      </div>

      <div className="company-stats">
        <StatsCard
          title="Total Companies"
          value={isLoadingSummary ? '...' : formatNumber(summary?.totalCompanies || 0)}
          change="+2.5%"
          changeType="increase"
          icon={<Building2 size={24} />}
          color="blue"
        />
        <StatsCard
          title="Active Companies"
          value={isLoadingSummary ? '...' : formatNumber(summary?.activeCompanies || 0)}
          change="+5.2%"
          changeType="increase"
          icon={<Building2 size={24} />}
          color="green"
        />
        <StatsCard
          title="Total Cards Issued"
          value={isLoadingSummary ? '...' : formatNumber(summary?.totalCardsIssued || 0)}
          change="+8.1%"
          changeType="increase"
          icon={<CreditCard size={24} />}
          color="orange"
        />
        <StatsCard
          title="Monthly Revenue"
          value={isLoadingSummary ? '...' : formatCurrency(summary?.monthlyRevenue || 0)}
          change="+12.3%"
          changeType="increase"
          icon={<TrendingUp size={24} />}
          color="purple"
        />
      </div>

      <div className="companies-section">
        <div className="section-header">
          <h2>All Companies</h2>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="companies-table">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Cards</th>
                <th>Balance</th>
                <th>Monthly Spend</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <tr key={company.id}>
                  <td>
                    <div className="company-info">
                      <div className="company-avatar">
                        {company.logoUrl ? (
                          <img 
                            src={company.logoUrl.startsWith('http') ? company.logoUrl : `http://localhost:3000${company.logoUrl}`} 
                            alt={`${company.legalName} logo`} 
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                          />
                        ) : (
                          <Building2 size={20} />
                        )}
                      </div>
                      <div className="company-details">
                        <div className="company-name-status">
                          <h3>{company.legalName}</h3>
                        </div>
                        <p className="company-contact">{company.contactEmail}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="metric-value">{formatNumber(company.cardCount ?? 0)}</span>
                  </td>
                  <td>
                    <span className="metric-value">{formatCurrency(company.balance ?? 0)}</span>
                    {company.walletBalance != null && company.cardBalance != null && (company.walletBalance > 0 || company.cardBalance > 0) && (
                      <span className="metric-subtext" style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        Wallet: {formatCurrency(company.walletBalance)} | Cards: {formatCurrency(company.cardBalance)}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="metric-value">{formatCurrency(company.monthlySpend ?? 0)}</span>
                    {company.totalSpend != null && company.totalSpend > 0 && company.monthlySpend === 0 && (
                      <span className="metric-subtext" style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        All-time: {formatCurrency(company.totalSpend)}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="company-actions">
                      <CompanyActionDropdown
                        company={company}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteCompany}
                        onView={handleViewClick}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Company Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Company"
        size="lg"
        footer={
          <>
            <Button variant="outline" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" form="add-company-form">Create Company</Button>
          </>
        }
      >
        <form id="add-company-form" onSubmit={handleAddCompany} className="modal-form">
          <div className="form-grid">
            <div className="form-group full-width">
              <label><Building2 size={16} /> Company Legal Name</label>
              <input
                type="text"
                required
                value={newCompanyForm.legalName}
                onChange={(e) => setNewCompanyForm({ ...newCompanyForm, legalName: e.target.value })}
                placeholder="e.g. Acme Corp Ltd"
              />
            </div>
            <div className="form-group">
              <label><Mail size={16} /> Contact Email</label>
              <input
                type="email"
                required
                value={newCompanyForm.contactEmail}
                onChange={(e) => setNewCompanyForm({ ...newCompanyForm, contactEmail: e.target.value })}
                placeholder="contact@acme.com"
              />
            </div>
            <div className="form-group">
              <label><Lock size={16} /> Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={newCompanyForm.password}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, password: e.target.value })}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label><Lock size={16} /> Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={newCompanyForm.confirmPassword}
                  onChange={(e) => setNewCompanyForm({ ...newCompanyForm, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group full-width">
              <label><Building2 size={16} /> Company Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setNewCompanyLogo(e.target.files[0]);
                  } else {
                    setNewCompanyLogo(null);
                  }
                }}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Company"
        size="md"
        footer={
          <>
            <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" form="edit-company-form">Save Changes</Button>
          </>
        }
      >
        <form id="edit-company-form" onSubmit={handleUpdateCompany} className="modal-form">
          <div className="form-grid">
            <div className="form-group full-width">
              <label><Building2 size={16} /> Company Legal Name</label>
              <input
                type="text"
                required
                value={editCompanyForm.legalName}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, legalName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label><Mail size={16} /> Contact Email</label>
              <input
                type="email"
                required
                value={editCompanyForm.contactEmail}
                onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contactEmail: e.target.value })}
              />
            </div>
            <div className="form-group full-width">
              <label><Building2 size={16} /> Update Company Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setEditCompanyLogo(e.target.files[0]);
                  } else {
                    setEditCompanyLogo(null);
                  }
                }}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen && !!selectedCompany}
        onClose={() => { setIsDetailsModalOpen(false); dispatch(clearSelectedCompany()); }}
        title="Company Profile"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => { setIsDetailsModalOpen(false); dispatch(clearSelectedCompany()); }}>Close</Button>
            <Button variant="primary" onClick={() => { setIsDetailsModalOpen(false); selectedCompany && handleEditClick(selectedCompany); }}>Edit Details</Button>
          </>
        }
      >
        {selectedCompany && (
          <div className="details-modal-container">
            <div className="details-hero">
              <div className="details-avatar-large">
                <Building2 size={40} />
              </div>
              <div className="details-title-section">
                <h1>{selectedCompany.legalName}</h1>
              </div>
            </div>
            <div className="details-grid-premium">
              <div className="detail-card">
                <label><Mail size={14} /> Contact Email</label>
                <p>{selectedCompany.contactEmail}</p>
              </div>
              <div className="detail-card">
                <label><TrendingUp size={14} /> Registration Date</label>
                <p>{new Date(selectedCompany.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="detail-card">
                <label><CreditCard size={14} /> Total Cards</label>
                <p>{formatNumber(selectedCompany.cardCount ?? 0)}</p>
              </div>
              <div className="detail-card">
                <label><Building2 size={14} /> Total Balance</label>
                <p>{formatCurrency(selectedCompany.balance ?? 0)}</p>
                {selectedCompany.walletBalance != null && selectedCompany.cardBalance != null && (selectedCompany.walletBalance > 0 || selectedCompany.cardBalance > 0) && (
                  <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                    Wallet: {formatCurrency(selectedCompany.walletBalance)} • Cards: {formatCurrency(selectedCompany.cardBalance)}
                  </span>
                )}
              </div>
              <div className="detail-card">
                <label><TrendingUp size={14} /> Monthly Spend</label>
                <p>{formatCurrency(selectedCompany.monthlySpend ?? 0)}</p>
              </div>
              {selectedCompany.totalSpend != null && selectedCompany.totalSpend > 0 && (
                <div className="detail-card">
                  <label><TrendingUp size={14} /> All-Time Spend</label>
                  <p>{formatCurrency(selectedCompany.totalSpend)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompanyPage;
